"use client";
import { Link } from "components/commons/Link";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { localizePath } from "src/utils/localizedRoutes";

type DocEntry = {
  to: string;
  group: string;
  title: string;
  description: string;
  rawBody: string;
  bodyBlob: string;
};

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const buildIndex = (messages: Record<string, unknown>): DocEntry[] =>
  Object.entries(messages)
    .filter(
      ([key, value]) =>
        !key.startsWith("layout/") &&
        typeof value === "object" &&
        value !== null,
    )
    .map(([key, value]) => {
      const fields = value as Record<string, unknown>;
      const bodyStrings = Object.entries(fields)
        .filter(
          ([fieldKey]) =>
            fieldKey.startsWith("details-") ||
            fieldKey.startsWith("heading-") ||
            fieldKey.startsWith("list-"),
        )
        .map(([, fieldValue]) => fieldValue)
        .filter((field): field is string => typeof field === "string");
      const rawBody = bodyStrings.join(" ");
      return {
        to: `/software/${key}`,
        group: (fields.group as string) ?? "",
        title: (fields.title as string) ?? "",
        description: (fields["metadata-description"] as string) ?? "",
        rawBody,
        bodyBlob: normalize(rawBody),
      };
    });

const SNIPPET_RADIUS = 60;

const extractSnippet = (
  rawText: string,
  normalizedText: string,
  queryN: string,
) => {
  const idx = normalizedText.indexOf(queryN);
  if (idx === -1) return null;
  const start = Math.max(0, idx - SNIPPET_RADIUS);
  const end = Math.min(rawText.length, idx + queryN.length + SNIPPET_RADIUS);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < rawText.length ? "…" : "";
  return `${prefix}${rawText.slice(start, end).trim()}${suffix}`;
};

const highlightSnippet = (snippet: string, queryN: string) => {
  const snippetN = normalize(snippet);
  const idx = snippetN.indexOf(queryN);
  if (idx === -1) return snippet;
  return (
    <>
      {snippet.slice(0, idx)}
      <mark className="bg-yellow-200 font-semibold text-black">
        {snippet.slice(idx, idx + queryN.length)}
      </mark>
      {snippet.slice(idx + queryN.length)}
    </>
  );
};

const SearchIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1 0 6.11 6.11a7.5 7.5 0 0 0 10.54 10.54Z"
    />
  </svg>
);

type DocsSearchProps = {
  className?: string;
};

const DocsSearch = ({ className }: DocsSearchProps) => {
  const t = useTranslations("layout/topbar");
  const router = useRouter();
  const [messages, setMessages] = useState<Record<string, unknown> | null>(
    null,
  );
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const loadedLocaleRef = useRef<string | null>(null);

  const ensureIndexLoaded = () => {
    if (loadedLocaleRef.current === router.locale) return;
    loadedLocaleRef.current = router.locale ?? null;
    import(`../../../lang/${router.locale}-docs.json`).then((mod) => {
      setMessages(mod.default);
    });
  };

  const index = useMemo(
    () => (messages ? buildIndex(messages) : []),
    [messages],
  );

  const results = useMemo(() => {
    const queryN = normalize(query.trim());
    if (!queryN) return [];
    return index
      .map((entry) => {
        const titleN = normalize(entry.title);
        const groupN = normalize(entry.group);
        const descriptionN = normalize(entry.description);
        let score = 0;
        if (titleN.includes(queryN)) score = 3;
        else if (descriptionN.includes(queryN)) score = 2;
        else if (groupN.includes(queryN)) score = 1;
        else if (entry.bodyBlob.includes(queryN)) score = 0.5;
        const snippet = extractSnippet(entry.rawBody, entry.bodyBlob, queryN);
        return { entry, score, snippet };
      })
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [index, query]);

  const closeSearch = () => {
    setFocused(false);
  };

  const handleFocus = () => {
    ensureIndexLoaded();
    setFocused(true);
  };

  useEffect(() => {
    if (!focused) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        closeSearch();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [focused]);

  return (
    <div ref={wrapperRef} className={twMerge("relative w-full max-w-full", className)}>
      <div className="absolute left-2.5 inset-y-0 flex items-center">
        <SearchIcon className="h-4 w-4 text-gray-800" />
      </div>
      <input
        type="text"
        value={query}
        onFocus={handleFocus}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && results[0]) {
            router.push(localizePath(results[0].entry.to, router.locale));
            closeSearch();
          }
          if (event.key === "Escape") {
            closeSearch();
          }
        }}
        placeholder={t("search-placeholder")}
        className="w-full rounded-md border border-gray-600 bg-white py-1.5 pl-8 pr-2 text-sm text-black placeholder:text-gray-800 focus:border-blue focus:outline-none focus:ring-1 focus:ring-blue"
      />
      <AnimatePresence>
        {focused && query.trim() !== "" && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute left-0 top-full z-20 mt-1 w-full max-h-[calc(100vh-8rem)] overflow-y-auto rounded-md border border-gray-600 bg-white shadow-lg"
          >
            {results.length > 0 ? (
              <ul className="divide-y divide-gray-400">
                {results.map(({ entry, snippet }) => (
                  <li key={entry.to}>
                    <Link
                      href={localizePath(entry.to, router.locale)}
                      onClick={closeSearch}
                      className="block px-3 py-2 text-sm hover:bg-gray-400"
                    >
                      <div>
                        <span className="font-semibold text-black">
                          {entry.title}
                        </span>
                        <span className="text-black"> | {entry.group}</span>
                      </div>
                      {snippet && (
                        <p className="mt-0.5 text-xs text-black">
                          {highlightSnippet(snippet, normalize(query.trim()))}
                        </p>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-3 py-2 text-sm text-gray-800">
                {t("search-no-results")}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocsSearch;
